import { useContext } from 'react';
import { AlertContext, EAlertVariant } from 'src/context/AlertContext';
import { ERoles } from 'src/config/enums';
import { isNumeric } from 'src/utils/isNumeric';
import { OnyxError } from 'src/helpers/OnyxError';
import { useStore } from 'react-redux';
import { userRoleIsAdmin } from 'src/helpers/user';
import { AxiosError } from 'axios';
import {
    EApiErrorSource,
    getApiErrorTrackingProperties
} from 'src/services/api/api.error';
import type { TApiErrorResponse } from 'src/services/api/api.error';

type TReturn = { validateResponse: (data: unknown) => unknown };
type TStoreState = { user?: { roles?: ERoles[] } };
type TApiResponseData = Partial<TApiErrorResponse> & Record<string, unknown>;

const shouldSkipErrorTracking = (data: Partial<TApiErrorResponse> & Record<string, unknown>) =>
    data.isNetworkError ||
    data.isTimeout ||
    data.status === 0 ||
    data.statusCode === 0 ||
    data.name === 'NetworkError' ||
    (!!data.code && [AxiosError.ECONNABORTED, AxiosError.ETIMEDOUT].includes(data.code));

const getSafeBackendMessage = (data: Partial<TApiErrorResponse>): string | undefined => {
    const statusCode = data.status || data.statusCode;
    const message = data.title ?? data.error;

    if (!message || typeof message !== 'string') return undefined;
    if (!statusCode || statusCode >= 500) return undefined;
    if (data.apiErrorSource === EApiErrorSource.Auth) return undefined;
    if (data.isNetworkError || data.isTimeout) return undefined;

    return message;
};

const getApiAlertTitle = (data: Partial<TApiErrorResponse>): string => {
    if (data.isAuthError) return 'Access Required';
    if (data.isNetworkError || data.isTimeout) return 'Connection issue';

    const endpoint = data.apiEndpoint ?? '';

    if (endpoint.includes('appointment_time')) return "We couldn't load booking availability";
    if (endpoint.includes('clinic_attendance/appointment_state')) {
        return 'Appointment status could not be updated';
    }
    if (
        endpoint.includes('patient_profile') ||
        endpoint.includes('reception/patient') ||
        endpoint.includes('patient/:id')
    ) {
        return 'Patient lookup failed';
    }
    if (data.apiErrorSource === EApiErrorSource.Cliniko) return 'Cliniko API error';

    return 'Something went wrong';
};

const getApiAlertMessage = (data: Partial<TApiErrorResponse>): string => {
    if (data.isAuthError) {
        return 'Please sign in again or ask an administrator to check your access.';
    }

    if (data.isNetworkError || data.isTimeout) {
        return 'Please check your connection and try again.';
    }

    return getSafeBackendMessage(data) ?? 'Please try again or contact support.';
};

export const useValidateResponse = (): TReturn => {
    const { showAlert } = useContext(AlertContext);
    let store: ReturnType<typeof useStore> | undefined;
    try {
        store = useStore();
    } catch (e) {
        // Not in a store context
    }

    const validateResponse = (data: unknown): unknown => {
        const { user } = (store?.getState() as TStoreState | undefined) ?? { user: {} };

        if (!data || typeof data !== 'object') {
            return data;
        }

        const responseData = data as TApiResponseData;
        const rawStatusCode = responseData?.status || responseData?.statusCode;
        const statusCode =
            rawStatusCode !== undefined && isNumeric(rawStatusCode)
                ? Number(rawStatusCode)
                : rawStatusCode;
        if (
            (statusCode && isNumeric(statusCode) && Math.floor(statusCode / 100) !== 2) ||
            responseData.error
        ) {
            const isAuthError =
                responseData.isAuthError === true || statusCode === 401 || statusCode === 403;
            const alertData = { ...responseData, isAuthError };
            const error = {
                type: EAlertVariant.Error,
                header: isAuthError ? 'Authentication Error' : 'API error',
                title: getApiAlertTitle(alertData),
                message: getApiAlertMessage(alertData),
                skipErrorTracking: shouldSkipErrorTracking(responseData),
                trackingProperties: getApiErrorTrackingProperties(responseData)
            };

            if (!!showAlert && statusCode === 429 && user?.roles && userRoleIsAdmin(user.roles)) {
                showAlert(error);
            }

            throw new OnyxError(error);
        }
        return data;
    };

    return { validateResponse };
};
